'use client';

import { useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import {
  ArrowRight,
  Building2,
  Calendar,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

// Lightbox de galería (item 14, 2026-07-05): carga solo al primer tap en la
// foto — evita SWR + DOM del modal en cada card del listado.
const ApartmentGalleryModal = dynamic(
  () => import('@/components/apartments/ApartmentGalleryModal'),
  { ssr: false }
);
import type { Apartment } from '@/types/apartment';
import { getDisplayName } from '@/types/apartment';
import type { EnrichedAvailability } from '@/lib/beds24/types';
import { companyConfig } from '@/lib/config-loader';
import { getBlurDataUrl, versionedImageUrl } from '@/lib/image-version';
import type { CTAEventProps } from '@/lib/analytics/posthog';
import { fireWspReservasConversion } from '@/lib/analytics/gtag-conversion';
import { openTrackedWhatsApp } from '@/lib/whatsapp/open-tracked-whatsapp';
import ApartmentCardFrame from '@/components/apartments/card/ApartmentCardFrame';
import ApartmentCardMediaOverlay from '@/components/apartments/card/ApartmentCardMediaOverlay';
import ApartmentCardMetaLine from '@/components/apartments/card/ApartmentCardMetaLine';
import {
  formatMoney,
  formatRoundedUpToThousand,
  getCarouselImages,
} from '@/components/apartments/card/apartment-card-utils';

type AvailabilityCardItem =
  | EnrichedAvailability
  | {
      apartment: Apartment;
      roomId?: number;
      totalPrice?: number;
      pricePerNight?: number;
      availableNights?: number;
      restrictionReason?: string;
    };

interface AvailabilityApartmentCardProps {
  item: AvailabilityCardItem;
  arrival: string;
  departure: string;
  guests: number;
  nights: number;
  availableApartmentIds?: string[];
  ctaAttribution: CTAEventProps;
  ctaPosition: string;
  ctaLabel?: string;
  isOnRequest?: boolean;
  restrictionLabel?: string;
  priority?: boolean;
}

function formatDate(value: string): string {
  if (!value) return '';
  return new Date(`${value}T00:00:00`).toLocaleDateString('es-CO', {
    day: 'numeric',
    month: 'short',
  });
}

function buildDetailUrl(
  apartmentId: string,
  params: {
    arrival: string;
    departure: string;
    guests: number;
    availableApartmentIds?: string[];
    building?: string;
    neighborhood?: string;
    source?: string;
  }
): string {
  const search = new URLSearchParams();
  if (params.arrival) search.set('checkIn', params.arrival);
  if (params.departure) search.set('checkOut', params.departure);
  if (params.guests) search.set('guests', String(params.guests));
  if (params.availableApartmentIds?.length) {
    search.set('feed', params.availableApartmentIds.slice(0, 50).join(','));
  }
  if (params.building) search.set('building', params.building);
  if (params.neighborhood) search.set('neighborhood', params.neighborhood);
  if (params.source) search.set('source', params.source);
  const qs = search.toString();
  return `/apartamentos/${apartmentId}${qs ? `?${qs}` : ''}`;
}

export default function AvailabilityApartmentCard({
  item,
  arrival,
  departure,
  guests,
  nights,
  availableApartmentIds,
  ctaAttribution,
  ctaPosition,
  ctaLabel = 'Reservar por WhatsApp',
  isOnRequest = false,
  restrictionLabel,
  priority = false,
}: AvailabilityApartmentCardProps) {
  const { apartment } = item;
  const [imageIndex, setImageIndex] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const images = useMemo(() => getCarouselImages(apartment), [apartment]);
  const imageCount = images.length;
  const currentImage = images[imageIndex];
  const displayName = getDisplayName(apartment);
  const cardNights = item.availableNights ?? nights;
  const hasPrice = typeof item.totalPrice === 'number' && item.totalPrice > 0;

  const reviewStats = apartment.reviewStats || null;
  const showSecondaryWhatsapp = isOnRequest || Boolean(restrictionLabel);

  const detailUrl = useMemo(
    () =>
      buildDetailUrl(apartment.id, {
        arrival,
        departure,
        guests,
        availableApartmentIds,
        building: ctaAttribution.building_id,
        neighborhood: ctaAttribution.neighborhood,
        source: ctaAttribution.source,
      }),
    [
      apartment.id,
      arrival,
      departure,
      guests,
      availableApartmentIds,
      ctaAttribution.building_id,
      ctaAttribution.neighborhood,
      ctaAttribution.source,
    ]
  );

  const whatsappMessage = useMemo(() => {
    const publicUrl = `https://www.pacartagena.com${detailUrl}`;
    const priceText = hasPrice
      ? ` Total cotizado: ${formatMoney(item.totalPrice!)} COP por ${cardNights} ${cardNights === 1 ? 'noche' : 'noches'}.`
      : '';
    return [
      `Hola, quiero reservar ${displayName}.`,
      arrival && departure
        ? `Fechas: ${formatDate(arrival)} al ${formatDate(departure)}.`
        : '',
      guests ? `Huéspedes: ${guests}.` : '',
      priceText,
      `Link: ${publicUrl}`,
    ]
      .filter(Boolean)
      .join(' ');
  }, [arrival, cardNights, departure, detailUrl, displayName, guests, hasPrice, item]);

  const whatsappHref = useMemo(
    () =>
      `https://wa.me/${companyConfig.contact.whatsapp.number}?text=${encodeURIComponent(whatsappMessage)}`,
    [whatsappMessage]
  );

  const goToImage = (direction: -1 | 1) => {
    if (imageCount <= 1) return;
    setImageIndex((current) => (current + direction + imageCount) % imageCount);
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStart.current === null) return;
    const t = event.changedTouches[0];
    const dx = touchStart.current.x - (t?.clientX ?? 0);
    const dy = touchStart.current.y - (t?.clientY ?? 0);
    touchStart.current = null;
    // Solo reaccionar si el gesto es horizontal y supera el threshold mínimo;
    // si Y domina X probablemente es scroll vertical y no debe paginar fotos.
    if (Math.abs(dx) < 34) return;
    if (Math.abs(dx) <= Math.abs(dy)) return;
    // Cancela el click sintético que el browser dispara post-touch, para que
    // el stretched <Link> debajo no navegue al detalle por error.
    event.preventDefault();
    goToImage(dx > 0 ? 1 : -1);
  };

  return (
    <>
    {/* Navegación en la MISMA pestaña (P1 2026-07-05): el overlay con
        target="_blank" perdía el back en móvil y falla silencioso en in-app
        browsers (IG/FB/Ads) — 7 rage clicks en 3 sesiones del 4-5 jul. */}
    <ApartmentCardFrame
      href={detailUrl}
      prefetch={false}
      // Brief 2026-05-18 P2: aria-label rico para que el wrapper accesible
      // tenga nombre específico — capacidad y total cotizado evitan que VoiceOver
      // anuncie "ver detalles" repetido en cada card.
      ariaLabel={[
        `Ver detalles de ${displayName}`,
        apartment.capacity?.guests
          ? `${apartment.capacity.guests} personas`
          : null,
        hasPrice ? `${formatMoney(item.totalPrice!)} total` : null,
        restrictionLabel ? restrictionLabel : null,
      ]
        .filter(Boolean)
        .join(', ')}
      onTouchStart={(event) => {
        const t = event.changedTouches[0];
        touchStart.current = t ? { x: t.clientX, y: t.clientY } : null;
      }}
      onTouchEnd={handleTouchEnd}
    >
      {/*
        El carrusel conserva handlers touch en el frame para que el overlay link
        no bloquee swipe horizontal.
      */}
      {/* === ZONA VISUAL (carrusel) === */}
      <div className="relative aspect-[3/2] w-full overflow-hidden bg-stone-100 sm:aspect-[4/3]">
        {currentImage ? (
          <Image
            src={versionedImageUrl(currentImage)}
            alt={`${displayName} en ${apartment.location?.neighborhood ?? 'Cartagena'}`}
            fill
            sizes="(max-width: 640px) 95vw, (max-width: 1024px) 45vw, 380px"
            className="pointer-events-none object-cover transition-transform duration-700 group-hover:scale-105"
            priority={priority}
            loading={priority ? 'eager' : 'lazy'}
            quality={78}
            placeholder="blur"
            blurDataURL={getBlurDataUrl(versionedImageUrl(currentImage))}
          />
        ) : (
          <div className="pointer-events-none flex h-full w-full items-center justify-center bg-stone-100 text-stone-500">
            <Building2 className="h-10 w-10" strokeWidth={1.5} />
          </div>
        )}

        <ApartmentCardMediaOverlay
          title={displayName}
          neighborhood={apartment.location?.neighborhood ?? 'Cartagena'}
          reviewStats={reviewStats}
          topRight={
            restrictionLabel ? (
              <span className="rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold text-amber-800 shadow-sm">
                {restrictionLabel}
              </span>
            ) : null
          }
        />

        {/* Móvil: tap en la foto abre la galería, no navega (item 14: sesión
            019f2ead pasó 6 min hojeando card por card con flechas chicas). La
            navegación al detalle queda en el CTA visible y el resto del card.
            Encima del overlay-link (z-10), debajo de flechas y dots (z-20). */}
        {imageCount > 0 && (
          <button
            type="button"
            onClick={() => setGalleryOpen(true)}
            className="absolute inset-0 z-[15] sm:hidden"
            aria-label={`Ver fotos de ${displayName}`}
          />
        )}

        {/* Dots de posición del carrusel (móvil) — el contador "1/N" era solo
            desktop-hover y en móvil no había señal de cuántas fotos hay. */}
        {imageCount > 1 && (
          <div className="pointer-events-none absolute bottom-2 left-1/2 z-20 flex -translate-x-1/2 items-center gap-1 sm:hidden">
            {images.map((src, i) => (
              <span
                key={src}
                className={`rounded-full transition-all duration-200 ${
                  i === imageIndex ? 'h-1.5 w-4 bg-white' : 'h-1.5 w-1.5 bg-white/60'
                }`}
              />
            ))}
          </div>
        )}

        {/* Chevrones: visibles en mobile para que el carrusel sea descubrible;
            en desktop aparecen al hover como control secundario.
            Solo se muestra la flecha, sin cápsula visible sobre la foto. */}
        {imageCount > 1 ? (
          <>
            <button
              type="button"
              aria-label="Foto anterior"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                goToImage(-1);
              }}
              className="absolute left-1 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center bg-transparent text-white drop-shadow-[0_2px_7px_rgba(0,0,0,0.75)] transition hover:text-white focus-visible:rounded-full focus-visible:bg-black/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
            >
              <ChevronLeft className="h-6 w-6" strokeWidth={2.35} />
            </button>
            <button
              type="button"
              aria-label="Siguiente foto"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                goToImage(1);
              }}
              className="absolute right-1 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center bg-transparent text-white drop-shadow-[0_2px_7px_rgba(0,0,0,0.75)] transition hover:text-white focus-visible:rounded-full focus-visible:bg-black/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
            >
              <ChevronRight className="h-6 w-6" strokeWidth={2.35} />
            </button>
            <span className="pointer-events-none absolute bottom-3 right-3 z-20 hidden rounded-full bg-black/45 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm transition-opacity sm:block sm:opacity-0 sm:group-hover:opacity-100">
              {imageIndex + 1} / {imageCount}
            </span>
          </>
        ) : null}
      </div>

      {/* === ZONA INFO === */}
      <div className="p-4">
        {/* Precio - misma jerarquía que ApartmentCard */}
        {isOnRequest ? (
          <div className="mb-2 flex h-[52px] flex-col justify-center">
            <span className="w-fit rounded-lg bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-700">
              Ver disponibilidad
            </span>
          </div>
        ) : hasPrice ? (
          <div className="mb-2 space-y-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium text-gray-600">
                Total por {cardNights} {cardNights === 1 ? 'noche' : 'noches'}:
              </span>
              <span className="text-lg font-semibold text-gray-900 tabular-nums">
                {formatMoney(item.totalPrice!)}
              </span>
            </div>
            {typeof item.pricePerNight === 'number' ? (
              <div className="text-xs text-gray-600">
                Reserva con solo{' '}
                <span className="font-semibold text-gray-950 tabular-nums">
                  {formatRoundedUpToThousand(item.pricePerNight)}
                </span>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="mb-2">
            <div className="flex items-baseline gap-1.5">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Precio
              </span>
              <span className="text-base font-semibold text-gray-900">según temporada</span>
            </div>
            <div className="mt-0.5 flex items-center gap-1 text-xs text-gray-500">
              <Calendar className="h-3 w-3" strokeWidth={2.5} />
              <span>Consúltanos por WhatsApp</span>
            </div>
          </div>
        )}

        <ApartmentCardMetaLine
          tipo={apartment.tipo}
          bedrooms={apartment.capacity.bedrooms}
          guests={apartment.capacity.guests}
          floor={apartment.piso}
        />

        {/*
          CTA WhatsApp sutil — SOLO cuando hay contexto que lo amerite:
          - isOnRequest (sin Beds24 → consulta manual obligada)
          - restrictionLabel (mínimo de noches / fecha bloqueada → pedir excepción)

          En el caso normal, la card entera ya navega al detalle, donde está el
          CTA principal + sticky mobile. Decisión validada con founder 2026-05-11.
        */}
        <div className="mt-3 flex items-center justify-between gap-3">
          {showSecondaryWhatsapp ? (
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => {
                event.preventDefault();
                openTrackedWhatsApp(whatsappMessage, {
                  ...ctaAttribution,
                  apartment_id: apartment.id,
                  apartment_name: displayName,
                  num_adults: guests,
                  cta_position: ctaPosition,
                  cta_label: ctaLabel,
                  has_pricing: hasPrice,
                });
                // Whitelist interna en el helper: solo dispara para
                // reservas_on_request_card / reservas_min_stay_card / reservas_checkinout_card.
                // reservas_available_card NO dispara (flujo normal espera checkout Bold).
                fireWspReservasConversion({
                  cta_position: ctaPosition,
                  arrival,
                  departure,
                  num_adults: guests,
                  apartment_id: apartment.id,
                });
              }}
              className="relative z-20 inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900 transition-colors hover:text-emerald-800"
            >
              <span>{ctaLabel}</span>
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.25} />
            </a>
          ) : null}

          {/* Affordance de navegación (item 13): la única acción era un overlay
              invisible — por eso tocaban fotos y flechas sin saber qué navega.
              pointer-events-none: el tap pasa al overlay-link de todo el card. */}
          <span
            aria-hidden="true"
            className="pointer-events-none relative z-20 ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700"
          >
            Ver apartamento
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.25} />
          </span>
        </div>
      </div>
    </ApartmentCardFrame>

    {/* Fuera del <article>: su hover:-translate-y-px crea containing block y
        atraparía el lightbox `fixed`. Carga perezosa: solo al primer tap. */}
    {galleryOpen && (
      <ApartmentGalleryModal
        apartmentId={apartment.id}
        apartmentName={displayName}
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
      />
    )}
    </>
  );
}
